import { useState, useEffect, useRef } from "react";
import { PitchDetector } from "pitchy";

const GOLD = "#F9A826";
const BLUE = "#0f1729";

const CORDAS = [
    { nome: "6ª E", nota: "E", oitava: 2, freq: 82.41 },
    { nome: "5ª A", nota: "A", oitava: 2, freq: 110.00 },
    { nome: "4ª D", nota: "D", oitava: 3, freq: 146.83 },
    { nome: "3ª G", nota: "G", oitava: 3, freq: 196.00 },
    { nome: "2ª B", nota: "B", oitava: 3, freq: 246.94 },
    { nome: "1ª E", nota: "E", oitava: 4, freq: 329.63 },
];

const NOTAS = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

function freqParaNota(freq) {
    if (!freq || freq <= 0) return null;
    const semitons = 12 * Math.log2(freq / 440);
    const notaIdx = ((Math.round(semitons) % 12) + 12) % 12;
    const oitava = Math.floor((Math.round(semitons) + 69) / 12);
    const freqIdeal = 440 * Math.pow(2, Math.round(semitons) / 12);
    const cents = 1200 * Math.log2(freq / freqIdeal);
    return { nota: NOTAS[notaIdx], oitava, cents, freqIdeal: Math.round(freqIdeal) };
}

function notaParaFreq(nota, oitava) {
    const idx = NOTAS.indexOf(nota);
    if (idx === -1) return 440;
    return 440 * Math.pow(2, ((idx - 9) + (oitava - 4) * 12) / 12);
}

function AgulhaAfinador({ cents, cor, ativo }) {
    const angulo = Math.max(-45, Math.min(45, (cents || 0) * 0.9));
    const W = 260, H = 150, CX = W / 2, CY = H;
    const RAIO = 130;
    const marcacoes = [-45, -30, -15, 0, 15, 30, 45];
    const rad = (angulo * Math.PI) / 180;
    const x2 = CX + (RAIO - 10) * Math.sin(rad);
    const y2 = CY - (RAIO - 10) * Math.cos(rad);

    return (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
            <svg width={W} height={H} style={{ overflow: "visible" }}>
                {marcacoes.map(grau => {
                    const r = (grau * Math.PI) / 180;
                    const x1 = CX + (RAIO - 14) * Math.sin(r);
                    const y1 = CY - (RAIO - 14) * Math.cos(r);
                    const xm = CX + RAIO * Math.sin(r);
                    const ym = CY - RAIO * Math.cos(r);
                    return (
                        <line key={grau} x1={x1} y1={y1} x2={xm} y2={ym}
                              stroke={grau === 0 ? "#4caf50" : "#ffffff33"}
                              strokeWidth={grau === 0 ? 3 : 1.5}
                        />
                    );
                })}
                <circle cx={CX} cy={CY - RAIO - 8} r={4} fill="#4caf50" />
                <text x={10} y={CY - 5} fill="#ffffff55" fontSize={16} fontWeight="bold">♭</text>
                <text x={W - 22} y={CY - 5} fill="#ffffff55" fontSize={16} fontWeight="bold">♯</text>
                {ativo && (
                    <>
                        <line x1={CX} y1={CY} x2={x2} y2={y2} stroke={cor + "33"} strokeWidth={10} strokeLinecap="round" />
                        <line x1={CX} y1={CY} x2={x2} y2={y2} stroke={cor} strokeWidth={3} strokeLinecap="round" />
                    </>
                )}
                <circle cx={CX} cy={CY} r={9} fill={ativo ? cor : "#ffffff22"} />
                <circle cx={CX} cy={CY} r={4} fill="#060c1a" />
            </svg>
        </div>
    );
}

export default function Afinador({ tema, abaAberta = false }) {
    const T = tema;
    const [modo, setModo] = useState("violao");
    const [notaSel, setNotaSel] = useState("A");
    const [oitavaSel, setOitavaSel] = useState(4);
    const [freq, setFreq] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState(null);
    const [centsDisplay, setCentsDisplay] = useState(0);
    const [ativo, setAtivo] = useState(false);
    const [aberto, setAberto] = useState(false);

    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const detectorRef = useRef(null);
    const centsSmoothed = useRef(0);

    const parar = () => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
            audioCtxRef.current.close();
        }
        audioCtxRef.current = null;
        analyserRef.current = null;
        detectorRef.current = null;
        centsSmoothed.current = 0;
        setAtivo(false);
        setFreq(null);
        setResultado(null);
        setCentsDisplay(0);
    };

    const iniciar = async () => {
        try {
            setErro(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
            });
            streamRef.current = stream;
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            audioCtxRef.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 4096;
            analyserRef.current = analyser;
            detectorRef.current = PitchDetector.forFloat32Array(4096);
            ctx.createMediaStreamSource(stream).connect(analyser);
            setAtivo(true);
            detectar();
        } catch (e) {
            setErro("Permita o acesso ao microfone.");
        }
    };

    const fechar = () => {
        parar();
        setAberto(false);
    };

    const abrir = () => {
        setAberto(true);
        setTimeout(() => iniciar(), 150);
    };

    // Único useEffect — inicia quando é aba, para ao sair
    useEffect(() => {
        if (abaAberta) {
            setTimeout(() => iniciar(), 300);
        }
        return () => parar();
    }, [abaAberta]);

    const detectar = () => {
        const analyser = analyserRef.current;
        const detector = detectorRef.current;
        const ctx = audioCtxRef.current;
        if (!analyser || !detector || !ctx) return;
        const buffer = new Float32Array(analyser.fftSize);

        const loop = () => {
            analyser.getFloatTimeDomainData(buffer);
            let rms = 0;
            for (let i = 0; i < buffer.length; i++) rms += buffer[i] * buffer[i];
            rms = Math.sqrt(rms / buffer.length);

            if (rms > 0.01) {
                const [pitchHz, clarity] = detector.findPitch(buffer, ctx.sampleRate);
                if (pitchHz > 60 && pitchHz < 1200 && clarity > 0.85) {
                    const res = freqParaNota(pitchHz);
                    if (res) {
                        const alpha = 0.3;
                        centsSmoothed.current = centsSmoothed.current * (1 - alpha) + res.cents * alpha;
                        setFreq(Math.round(pitchHz * 10) / 10);
                        setResultado({ ...res });
                        setCentsDisplay(centsSmoothed.current);
                    }
                }
            } else {
                centsSmoothed.current *= 0.8;
                setCentsDisplay(centsSmoothed.current);
                if (Math.abs(centsSmoothed.current) < 0.3) {
                    setFreq(null);
                    setResultado(null);
                }
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        loop();
    };

    const cordaMaisProxima = () => {
        if (!freq) return null;
        let melhor = null, menorDiff = Infinity;
        CORDAS.forEach(c => {
            const diff = Math.abs(freq - c.freq);
            if (diff < menorDiff) { menorDiff = diff; melhor = c; }
        });
        return melhor;
    };

    const corda = cordaMaisProxima();
    const freqAlvo = notaParaFreq(notaSel, oitavaSel);
    const centsAlvo = freq ? 1200 * Math.log2(freq / freqAlvo) : 0;
    const centsAtual = modo === "violao" ? centsDisplay : centsAlvo;
    const afinado = Math.abs(centsAtual) < 5;
    const quase = Math.abs(centsAtual) < 15;
    const cor = !freq ? "#ffffff44" : afinado ? "#4caf50" : quase ? GOLD : "#f44336";

    const conteudo = (
        <div style={{ padding: 20 }}>
            {erro && (
                <div style={{ color: "#f44336", fontSize: 12, marginBottom: 10, textAlign: "center", background: "#f4433318", borderRadius: 8, padding: 8 }}>
                    🎤 {erro}{" "}
                    <button onClick={iniciar} style={{ background: "none", border: "none", color: GOLD, cursor: "pointer", fontWeight: 700 }}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {/* Seletor modo */}
            <div style={{ display: "flex", background: "#ffffff0e", borderRadius: 10, padding: 3, marginBottom: 16, border: "1px solid #ffffff12" }}>
                {[["violao", "🎸 Violão"], ["cromatico", "🎵 Cromático"]].map(([id, label]) => (
                    <button key={id} onClick={() => setModo(id)} style={{
                        flex: 1, padding: "7px 0", border: "none", borderRadius: 8,
                        background: modo === id ? GOLD : "transparent",
                        color: modo === id ? BLUE : "#ffffff88",
                        fontWeight: modo === id ? 700 : 500, fontSize: 12, cursor: "pointer"
                    }}>{label}</button>
                ))}
            </div>

            {/* Nota */}
            <div style={{ textAlign: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 64, fontWeight: 900, color: cor, lineHeight: 1, transition: "color 0.15s" }}>
          {modo === "violao" ? (freq && corda ? corda.nota : "—") : notaSel}
        </span>
                {freq && resultado && (
                    <span style={{ fontSize: 22, color: "#ffffff44", marginLeft: 4 }}>
            {modo === "violao" ? resultado.oitava : oitavaSel}
          </span>
                )}
                <div style={{ fontSize: 12, color: "#ffffff44", marginTop: 2 }}>
                    {freq ? `${freq} Hz` : "🎵 Toque uma nota..."}
                </div>
            </div>

            {/* Agulha */}
            <div style={{ background: "#060c1a", borderRadius: 16, padding: "12px 0 8px", border: "1px solid #ffffff0e", marginBottom: 12 }}>
                <AgulhaAfinador cents={centsAtual} cor={cor} ativo={!!freq} />
                <div style={{ textAlign: "center", minHeight: 24 }}>
                    {freq && (
                        <div style={{ fontSize: 14, fontWeight: 700, color: cor, transition: "color 0.15s" }}>
                            {afinado ? "✓ Afinado!" : centsAtual > 0 ? "Afrouxar ◀" : "▶ Apertar"}
                            {!afinado && (
                                <span style={{ fontSize: 10, fontWeight: 400, color: "#ffffff44", marginLeft: 6 }}>
                  {Math.round(centsAtual) > 0 ? "+" : ""}{Math.round(centsAtual)} cents
                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Cordas violão */}
            {modo === "violao" && (
                <div style={{ display: "flex", gap: 4 }}>
                    {CORDAS.map((c, i) => {
                        const ehEssa = corda?.nome === c.nome && !!freq;
                        return (
                            <div key={i} style={{
                                flex: 1, padding: "6px 2px", borderRadius: 8, textAlign: "center",
                                border: `2px solid ${ehEssa ? cor : "#ffffff18"}`,
                                background: ehEssa ? `${cor}18` : "#ffffff08",
                                transition: "all 0.15s"
                            }}>
                                <div style={{ fontSize: 13, fontWeight: 800, color: ehEssa ? cor : "#ffffff44" }}>{c.nota}</div>
                                <div style={{ fontSize: 8, color: "#ffffff33" }}>{c.nome.split("ª")[0]}ª</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Cromático */}
            {modo === "cromatico" && (
                <div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                        {NOTAS.map(n => (
                            <button key={n} onClick={() => setNotaSel(n)} style={{
                                padding: "5px 10px", borderRadius: 7,
                                border: `1px solid ${notaSel === n ? GOLD : "#ffffff22"}`,
                                background: notaSel === n ? GOLD : "#ffffff0a",
                                color: notaSel === n ? BLUE : "white",
                                fontSize: 12, fontWeight: 600, cursor: "pointer"
                            }}>{n}</button>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: "#ffffff55" }}>Oitava:</span>
                        {[2,3,4,5].map(o => (
                            <button key={o} onClick={() => setOitavaSel(o)} style={{
                                padding: "3px 10px", borderRadius: 6,
                                border: `1px solid ${oitavaSel === o ? GOLD : "#ffffff22"}`,
                                background: oitavaSel === o ? GOLD : "#ffffff0a",
                                color: oitavaSel === o ? BLUE : "white",
                                fontSize: 12, fontWeight: 600, cursor: "pointer"
                            }}>{o}</button>
                        ))}
                        <span style={{ fontSize: 10, color: "#ffffff44" }}>≈ {Math.round(freqAlvo)} Hz</span>
                    </div>
                </div>
            )}
        </div>
    );

    // ── Modo ABA ──
    if (abaAberta) {
        return (
            <div style={{ background: BLUE, borderRadius: 20, border: "1px solid #ffffff18", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #ffffff12", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${GOLD},#FFD166)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🎸</div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "white" }}>Afinador</div>
                        <div style={{ fontSize: 11, color: "#ffffff55" }}>
                            {ativo ? (freq ? `${freq} Hz detectado` : "Aguardando som...") : "Iniciando microfone..."}
                        </div>
                    </div>
                </div>
                {conteudo}
            </div>
        );
    }

    // ── Modo BOTÃO + MODAL ──
    return (
        <>
            <button onClick={abrir} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: T.bgCard, border: `1px solid ${T.borda}`,
                borderRadius: 10, padding: "7px 14px",
                color: T.texto, fontSize: 13, fontWeight: 600, cursor: "pointer"
            }}>
                🎸 Afinador
            </button>

            {aberto && (
                <div onClick={fechar} style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                    zIndex: 9000, display: "flex", alignItems: "center",
                    justifyContent: "center", padding: 20
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        width: "100%", maxWidth: 360,
                        background: BLUE, borderRadius: 24,
                        border: "1px solid #ffffff18",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.9)"
                    }}>
                        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #ffffff12" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${GOLD},#FFD166)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🎸</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: "white" }}>Afinador</div>
                            </div>
                            <button onClick={fechar} style={{ width: 32, height: 32, borderRadius: "50%", background: "#ffffff18", border: "none", color: "white", fontSize: 16, cursor: "pointer" }}>✕</button>
                        </div>
                        {conteudo}
                    </div>
                </div>
            )}
        </>
    );
}